import * as React from 'react';
import { snap, story, visualize } from 'storybook-snapper';
import {
  AddItem,
  AddItemAlignment,
  AddItemIconSize,
  AddItemDirection,
} from '..';
import { classes } from './AddItem.visual.st.css';
import { VisualAsync } from '../../test/visual/VisualAsync';
import { addItemDriverFactory } from './AddItem.driver';

const CONTAINER_SIZES_MAP = {
  [AddItemIconSize.small]: { width: '250px', height: '65px' },
  [AddItemIconSize.medium]: { width: '250px', height: '70px' },
  [AddItemIconSize.large]: { width: '250px', height: '100px' },
  [AddItemIconSize.xLarge]: { width: '250px', height: '130px' },
};

visualize('AddItem', () => {
  story('simple', () => {
    snap('With regular text', <AddItem>Add Item</AddItem>);
    snap(
      'With child node',
      <AddItem>
        <div>Add Item div</div>
      </AddItem>,
    );
  });

  story('Sizes', () => {
    Object.values(AddItemIconSize).map((size) => {
      Object.values(AddItemDirection).map((direction) => {
        snap(
          `${size} / ${direction}`,
          <div
            style={{
              width: CONTAINER_SIZES_MAP[size].width,
              height: CONTAINER_SIZES_MAP[size].height,
            }}
          >
            <AddItem iconSize={size} direction={direction}>
              Add Item
            </AddItem>
          </div>,
        );
      });
    });
  });

  story('Alignments', () => {
    Object.values(AddItemAlignment).map((alignment) => {
      snap(alignment, <AddItem alignment={alignment}>Add Item</AddItem>);
    });
  });

  story('only icons', () => {
    Object.values(AddItemIconSize).map((size) => {
      snap(
        size,
        <div
          style={{
            width: CONTAINER_SIZES_MAP[size].width,
            height: CONTAINER_SIZES_MAP[size].height,
          }}
        >
          <AddItem iconSize={size} />
        </div>,
      );
    });
  });

  story('states', () => {
    snap('disabled', <AddItem disabled>Add Item</AddItem>);
    snap('hasError', <AddItem hasError>Add Item</AddItem>);
  });

  story('styles overrides', () => {
    snap('default', <AddItem className={classes.root}>Add Item</AddItem>);
  });

  story('focus', () => {
    const its = [
      { it: 'regular', props: {} },
      { it: 'disabled', props: { disabled: true } },
    ];
    its.forEach(({ it, props }) => {
      snap(it, (done) => (
        <VisualAsync
          driverFactory={addItemDriverFactory}
          onDone={done}
          action={(driver) => driver.click()}
        >
          <AddItem {...props}></AddItem>
        </VisualAsync>
      ));
    });
  });

  const WstTest = ({
    variables,
  }: {
    variables: Record<`--wst-${string}`, string>;
  }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: '20px',
        ...variables,
      }}
    >
      <AddItem>Add Item</AddItem>
      <AddItem disabled>Disabled Add Item</AddItem>
      <AddItem hasError>Error Add Item</AddItem>
      <AddItem iconSize={AddItemIconSize.small}>Small Add Item</AddItem>
      <AddItem iconSize={AddItemIconSize.large}>Large Add Item</AddItem>
    </div>
  );

  story('wst variables', () => {
    snap(
      'primary background',
      <WstTest
        variables={{
          '--wst-primary-background-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'links and actions color',
      <WstTest
        variables={{
          '--wst-links-and-actions-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'paragraph 2',
      <WstTest
        variables={{
          '--wst-paragraph-2-color-rgb': '0,255,0',
          '--wst-paragraph-2-line-height': '24px',
          '--wst-paragraph-2-font-family': 'Libre Baskerville',
          '--wst-paragraph-2-font-weight': 'bold',
          '--wst-paragraph-2-font-style': 'italic',
          '--wst-paragraph-2-font-variant': 'small-caps',
        }}
      />,
    );

    snap(
      'system disabled',
      <WstTest
        variables={{
          '--wst-system-disabled-color-rgb': '0,255,0',
        }}
      />,
    );

    snap(
      'system error',
      <WstTest
        variables={{
          '--wst-system-error-color-rgb': '0,255,0',
        }}
      />,
    );
  });
});
